import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import api from '@/lib/api';
import ImageUploader from '@/components/order/ImageUploader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, ArrowRight, Shield, FileText, ShoppingCart, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { trackAddToCart } from '@/lib/analytics';

export default function ItemSubmitPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const initCategory = params.get('category') || '';
  const initService = params.get('service') || '';

  const [step, setStep] = useState(1); // 1=details, 2=photos
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [categorySlug, setCategorySlug] = useState(initCategory);
  const [serviceFamily, setServiceFamily] = useState(initService);
  const [brandSlug, setBrandSlug] = useState('');
  const [itemTitle, setItemTitle] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [description, setDescription] = useState('');
  const [imageData, setImageData] = useState({ slotImages: {}, extraImages: [] });
  // Appraisal-specific intake
  const [appraisalPurpose, setAppraisalPurpose] = useState('');
  const [appraisalCondition, setAppraisalCondition] = useState('');
  const [appraisalProvenance, setAppraisalProvenance] = useState('');

  useSeoMeta({ seo_title: 'Submit Item - The Fashion Detective' });

  // Load categories, brands, services
  useEffect(() => {
    Promise.all([
      api.get('/catalog/categories'),
      api.get('/catalog/brands'),
      api.get('/catalog/services'),
    ]).then(([catRes, brandRes, svcRes]) => {
      setCategories(catRes.data);
      setBrands(brandRes.data);
      setServices(svcRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Load image slots when category changes
  useEffect(() => {
    if (!categorySlug) return;
    api.get(`/catalog/image-slots/${categorySlug}`)
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
  }, [categorySlug]);

  const selectedService = services.find(s => s.family === serviceFamily);
  const basePrice = selectedService?.base_price_cents || 2000;

  const handleAddToCart = () => {
    const slotKeys = Object.entries(imageData.slotImages).map(([slot, img]) => ({
      storage_key: img.storage_key, slot_key: slot,
    }));
    const extraKeys = imageData.extraImages.map(img => ({
      storage_key: img.storage_key, slot_key: 'extra', is_extra: true,
    }));

    addItem({
      service_family: serviceFamily,
      category_slug: categorySlug,
      category_name: categories.find(c => c.slug === categorySlug)?.name || categorySlug,
      brand_slug: brandSlug,
      brand_name: brands.find(b => b.slug === brandSlug)?.name || '',
      item_title: itemTitle,
      serial_number: serialNumber,
      description,
      is_expedited: false,
      wants_mailed_document: false,
      image_keys: slotKeys,
      extra_image_keys: extraKeys,
      base_price_cents: basePrice,
      slot_images_preview: imageData.slotImages,
      // Appraisal intake
      appraisal_purpose: serviceFamily === 'appraisal' ? appraisalPurpose : undefined,
      appraisal_condition: serviceFamily === 'appraisal' ? appraisalCondition : undefined,
      appraisal_provenance: serviceFamily === 'appraisal' ? appraisalProvenance : undefined,
    });

    trackAddToCart(serviceFamily, categorySlug);

    toast.success('Item added to cart!');
    navigate('/order/cart');
  };

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div data-testid="item-submit-page">
      <section style={{ background: 'linear-gradient(135deg, hsl(36 33% 98%) 0%, hsl(36 22% 94%) 55%, hsl(35 19% 88%) 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <Link to="/services" className="inline-flex items-center gap-1 text-xs text-muted-foreground no-underline hover:text-ink mb-4">
            <ArrowLeft className="h-3.5 w-3.5" /> Services
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-[-0.02em] text-ink mb-2">
            {step === 1 && !serviceFamily ? 'Start an Order' : 'Submit an Item'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {!serviceFamily ? 'Choose your service and category to begin.' : `Step ${step} of 2: ${step === 1 ? 'Item Details' : 'Upload Photos'}`}
          </p>
          {/* Progress bar */}
          <div className="flex gap-2 mt-4">
            <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-ink' : 'bg-stone'}`} />
            <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-ink' : 'bg-stone'}`} />
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Card className="bg-white border-stone/50">
              <CardContent className="p-6 space-y-5">
                {/* Service */}
                <div>
                  <Label className="text-xs">Service Type</Label>
                  <Select value={serviceFamily} onValueChange={setServiceFamily}>
                    <SelectTrigger className="mt-1" data-testid="service-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(s => (
                        <SelectItem key={s.family} value={s.family}>
                          <div className="flex items-center gap-2">
                            {s.family === 'authentication' ? <Shield className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                            {s.name} - ${(s.base_price_cents / 100).toFixed(0)}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category */}
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={categorySlug} onValueChange={setCategorySlug}>
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(c => (
                        <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Brand */}
                <div>
                  <Label className="text-xs">Brand</Label>
                  <Select value={brandSlug} onValueChange={setBrandSlug}>
                    <SelectTrigger className="mt-1" data-testid="brand-select">
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map(b => (
                        <SelectItem key={b.slug} value={b.slug}>{b.name}</SelectItem>
                      ))}
                      <SelectItem value="other">Other / Not Listed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Item Title */}
                <div>
                  <Label className="text-xs">Item Name / Title</Label>
                  <Input
                    value={itemTitle}
                    onChange={e => setItemTitle(e.target.value)}
                    placeholder="e.g. Classic Flap Bag, Submariner Date"
                    className="mt-1"
                    data-testid="item-title-input"
                  />
                </div>

                {/* Serial Number */}
                <div>
                  <Label className="text-xs">Serial Number <span className="text-muted-foreground">(optional)</span></Label>
                  <Input
                    value={serialNumber}
                    onChange={e => setSerialNumber(e.target.value)}
                    placeholder="If visible on item"
                    className="mt-1"
                    data-testid="serial-number-input"
                  />
                  {serialNumber.trim() && (
                    <p className="text-[10px] mt-1" style={{ color: '#A36A3A' }}>
                      Since you entered a serial number, a serial number photo will be required in the next step.
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label className="text-xs">Additional Details <span className="text-muted-foreground">(optional)</span></Label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Any details that may help our authenticators"
                    className="w-full mt-1 p-3 rounded-lg border border-stone text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-ring/30"
                    data-testid="description-textarea"
                  />
                </div>

                {/* Appraisal-specific fields */}
                {serviceFamily === 'appraisal' && (
                  <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/30 space-y-3">
                    <p className="text-xs font-medium" style={{ color: '#A36A3A' }}>Appraisal Details</p>
                    <div>
                      <Label className="text-xs">Appraisal Purpose</Label>
                      <Select value={appraisalPurpose} onValueChange={setAppraisalPurpose}>
                        <SelectTrigger className="mt-1" data-testid="appraisal-purpose-select">
                          <SelectValue placeholder="Select purpose" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="insurance">Insurance</SelectItem>
                          <SelectItem value="estate">Estate</SelectItem>
                          <SelectItem value="donation">Donation</SelectItem>
                          <SelectItem value="resale">Resale</SelectItem>
                          <SelectItem value="personal">Personal Knowledge</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Item Condition</Label>
                      <Select value={appraisalCondition} onValueChange={setAppraisalCondition}>
                        <SelectTrigger className="mt-1" data-testid="appraisal-condition-select">
                          <SelectValue placeholder="Select condition" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="excellent">Excellent — Like new, minimal signs of use</SelectItem>
                          <SelectItem value="very_good">Very Good — Light wear, fully functional</SelectItem>
                          <SelectItem value="good">Good — Moderate wear, all parts intact</SelectItem>
                          <SelectItem value="fair">Fair — Visible wear, may have minor issues</SelectItem>
                          <SelectItem value="poor">Poor — Significant wear or damage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Provenance / History <span className="text-muted-foreground">(optional)</span></Label>
                      <textarea
                        value={appraisalProvenance}
                        onChange={e => setAppraisalProvenance(e.target.value)}
                        placeholder="Where/when was the item purchased? Any documentation, receipts, or ownership history?"
                        className="w-full mt-1 p-2.5 rounded-lg border border-stone text-sm min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-ring/30"
                        data-testid="appraisal-provenance"
                      />
                    </div>
                  </div>
                )}

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Base price: </span>
                    <span className="font-semibold text-ink">${(basePrice / 100).toFixed(0)}</span>
                  </div>
                  <Button
                    onClick={() => setStep(2)}
                    className="gap-2 rounded-[12px] active:scale-[0.98]"
                    disabled={!serviceFamily || !categorySlug}
                    data-testid="next-photos-btn"
                  >
                    Next: Upload Photos <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Card className="bg-white border-stone/50">
              <CardContent className="p-6">
                <div className="mb-4">
                  <h2 className="font-display text-lg font-medium text-ink mb-1">Upload Photos</h2>
                  <p className="text-xs text-muted-foreground">
                    Upload clear, well-lit photos. Max 20MB per image. HEIC, JPEG, PNG, WebP accepted.
                  </p>
                </div>

                <ImageUploader
                  slots={slots}
                  onImagesChange={setImageData}
                  existingImages={imageData.slotImages}
                  categorySlug={categorySlug}
                />

                <Separator className="my-6" />

                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <Button
                    onClick={handleAddToCart}
                    className="gap-2 rounded-[12px] active:scale-[0.98]"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="h-4 w-4" /> Add to Cart
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
